import { apiClient } from "./client";
import type {
  BrowserAuthResponse,
  LoginRequest,
  RegisterRequest,
  ListSessionsResponse,
  RevokeSessionResponse,
  RevokeAllSessionsResponse,
  RequestPasswordResetRequest,
  RequestPasswordResetResponse,
  ValidateResetTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from "./types";

// Mirrors AuthService RPCs in console/proto/auth.proto.
//
// login/register/logout/refresh hit console-server's hand-written HTTP
// handlers (not grpc-gateway transcoding -- see auth.proto's TRANSPORT
// NOTE and types.ts's matching note), which set/clear HttpOnly cookies
// and never put a raw JWT in the JSON body. Nothing else in this module
// needs to know that distinction -- axios's withCredentials: true
// (already configured in client.ts) makes cookies flow automatically.

export async function login(req: LoginRequest): Promise<BrowserAuthResponse> {
  const { data } = await apiClient.post<BrowserAuthResponse>("/auth/login", req);
  return data;
}

export async function register(req: RegisterRequest): Promise<BrowserAuthResponse> {
  const { data } = await apiClient.post<BrowserAuthResponse>("/auth/register", req);
  return data;
}

export async function logout(): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>("/auth/logout");
  return data;
}

/**
 * Renews the session from the refresh-token cookie (sent automatically)
 * AND returns who's logged in -- there is no separate GetCurrentUser/
 * WhoAmI RPC, so this doubles as the app-bootstrap "am I logged in"
 * check main.tsx runs on startup (see auth.proto's RefreshTokenResponse
 * .User doc). A 401 here (no cookie, or an expired/revoked session) is
 * the normal "not logged in" case, not an error -- callers should treat
 * it the same as a false `success`, not surface it as a failure.
 */
export async function refresh(): Promise<BrowserAuthResponse> {
  const { data } = await apiClient.post<BrowserAuthResponse>("/auth/refresh");
  return data;
}

/**
 * Redeems the token from the emailed verification link. Public: whoever
 * holds the link proves control of the mailbox, signed in or not.
 */
export async function verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>("/auth/verify-email", { token });
  return data;
}

/** Asks for a fresh verification email for the signed-in user's own address. */
export async function resendVerification(): Promise<{ success: boolean; message: string }> {
  const { data } = await apiClient.post<{ success: boolean; message: string }>("/auth/resend-verification");
  return data;
}

/** Bootstraps a CSRF cookie before the first mutating request (e.g. before showing the login form). */
export async function fetchCsrfToken(): Promise<void> {
  await apiClient.get("/auth/csrf-token");
}

export async function requestPasswordReset(
  req: RequestPasswordResetRequest,
): Promise<RequestPasswordResetResponse> {
  const { data } = await apiClient.post<RequestPasswordResetResponse>(
    "/auth/request-password-reset",
    req,
  );
  return data;
}

export async function validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
  const { data } = await apiClient.get<ValidateResetTokenResponse>(
    `/auth/validate-reset-token/${encodeURIComponent(token)}`,
  );
  return data;
}

export async function resetPassword(req: ResetPasswordRequest): Promise<ResetPasswordResponse> {
  const { data } = await apiClient.post<ResetPasswordResponse>("/auth/reset-password", req);
  return data;
}

export async function listSessions(userId: string): Promise<ListSessionsResponse> {
  const { data } = await apiClient.get<ListSessionsResponse>(`/auth/sessions/${userId}`);
  return data;
}

export async function revokeSession(
  userId: string,
  sessionId: string,
): Promise<RevokeSessionResponse> {
  const { data } = await apiClient.delete<RevokeSessionResponse>(
    `/auth/sessions/${userId}/${sessionId}`,
  );
  return data;
}

export async function revokeAllSessions(
  userId: string,
  exceptSessionId?: string,
): Promise<RevokeAllSessionsResponse> {
  const { data } = await apiClient.post<RevokeAllSessionsResponse>(
    `/auth/sessions/${userId}/revoke-all`,
    { exceptSessionId },
  );
  return data;
}
